import {
  ISubSpecialty,
  ISuppliersResourceCategory,
  ITherapySuppliersResult,
  getSubSpecialties,
  getSupplierNames,
  getSupplierResources,
} from 'API/Suppliers/suppliers';
import { getGeneralOptions } from 'API/generalOptions/generalOptions';
import { SUPPLIERS_ID, SUPPLIERS_STEPS } from 'Enum/suppliers';
import { useDispatchApp } from 'Hooks';
import { ITherapySuppliers } from 'Interfaces/therapySuppliers';
import { setGeneralOptions } from 'Store';
import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { IAction, SuppliersReducer } from 'reducer/Suppliers/SuppliersReducer';

export interface ISuppliers {
  results: ITherapySuppliersResult[];
  filteredResults: ITherapySuppliersResult[];
}

export interface ITherapyFilters extends ITherapySuppliers {
  orderByAscendingAlphabet?: boolean;
  orderByDescendingAlphabet?: boolean;
}

interface ISuppliersContext {
  suppliersId: number;
  setSuppliersId: (index: number) => void;
  stepIndex: number;
  setStepIndex: (index: number) => void;
  suppliers: ISuppliers;
  dispatchSuppliers: React.Dispatch<IAction>;
  resources: ISuppliersResourceCategory;
  supplierNames: string[];
  subSpecialties: Partial<ISubSpecialty>;
  filters: Partial<ITherapyFilters>;
  setFilters: (filters: Partial<ITherapyFilters>) => void;
  resetFiltersOnSpecialtyChange: (specialty: number) => void;
  isAllFiltersModal: boolean;
  setIsAllFiltersModal: (activeModal: boolean) => void;
}

export const SuppliersContext = createContext<ISuppliersContext>({
  suppliersId: 0,
  setSuppliersId: () => {},
  stepIndex: 0,
  setStepIndex: () => {},
  suppliers: {
    results: [],
    filteredResults: [],
  },
  dispatchSuppliers: () => {},
  resources: {},
  supplierNames: [],
  subSpecialties: {},
  filters: {},
  setFilters: () => {},
  resetFiltersOnSpecialtyChange: () => {},
  isAllFiltersModal: false,
  setIsAllFiltersModal: () => {},
});

export const defaultFilters = {
  supName: '',
  specialty: null,
  subSpecial: null,
  area: null,
  region: null,
  city: null,
  accessible: null,
  gender: null,
  homeVisit: null,
  institute: null,
  lng: [],
  treatMethod: [],
  degree: [],
};

export const SuppliersProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [suppliersId, setSuppliersId] = useState<number>(
    SUPPLIERS_ID.MENTAL_THERAPY
  );
  const [stepIndex, setStepIndex] = useState<number>(SUPPLIERS_STEPS.LOBBY);
  const [suppliers, dispatchSuppliers] = useReducer(SuppliersReducer, {
    results: [],
    filteredResults: [],
  });
  const [resources, setResources] = useState<ISuppliersResourceCategory>({});
  const [supplierNames, setSupplierNames] = useState<string[]>([]);
  const [subSpecialties, setSubSpecialties] = useState<Partial<ISubSpecialty>>(
    {}
  );
  const [filters, setFilters] = useState<Partial<ITherapySuppliers>>({
    ...defaultFilters,
  });
  const [isAllFiltersModal, setIsAllFiltersModal] = useState<boolean>(false);

  const dispatch = useDispatchApp();

  const initResources = useCallback(async () => {
    const resources = await getSupplierResources();
    if (resources) setResources(resources);
  }, [setResources]);

  const initGeneralOptions = useCallback(async () => {
    const newGeneralOptions = await getGeneralOptions();
    dispatch(setGeneralOptions(newGeneralOptions ?? null));
  }, [dispatch]);

  useEffect(() => {
    initResources();
    initGeneralOptions();
  }, [initGeneralOptions, initResources]);

  const initSupplierNames = useCallback(async () => {
    const newNames = await getSupplierNames(filters.specialty!);
    if (newNames) setSupplierNames(newNames);
  }, [filters.specialty]);

  const initSubSpecialties = useCallback(async () => {
    const newSubSpecialties = await getSubSpecialties(filters.specialty!);
    if (newSubSpecialties) setSubSpecialties(newSubSpecialties);
  }, [filters.specialty]);

  useEffect(() => {
    if (filters.specialty) {
      initSupplierNames();
      initSubSpecialties();
    } else {
      setSupplierNames([]);
      setSubSpecialties({});
    }
  }, [filters.specialty, initSubSpecialties, initSupplierNames]);

  const resetFiltersOnSpecialtyChange = (specialty: number) => {
    setFilters({
      supName: '',
      specialty: specialty,
      subSpecial: null,
      area: null,
      region: null,
      city: null,
      accessible: null,
      gender: null,
      homeVisit: null,
      institute: null,
      lng: [],
      treatMethod: [],
      degree: [],
    });
  };

  return (
    <SuppliersContext.Provider
      value={{
        suppliersId,
        setSuppliersId,
        stepIndex,
        setStepIndex,
        suppliers,
        dispatchSuppliers,
        resources,
        supplierNames,
        subSpecialties,
        filters,
        setFilters,
        resetFiltersOnSpecialtyChange,
        isAllFiltersModal,
        setIsAllFiltersModal,
      }}
    >
      {children}
    </SuppliersContext.Provider>
  );
};
